import clsx from 'clsx'
import React from 'react'
import { AnimatePresence, motion } from 'motion/react'
import { useSearchParams } from 'react-router'
import { CATALOG_BASE_PATH } from '@/const'
import { TreeLink, TreeNavigatorDataSchema, TreeNavigatorUISchema } from './types'
import { FormValues } from '../FormComponent/types'
import { useTreeNavigatorGraph } from './useTreeNavigatorGraph'
import { ColumnHeader } from './TreeNavigatorContentV2ColumnHeader'
import { NodeCard } from './TreeNavigatorContentV2NodeCard'
import { TreeNavigatorLegend } from './TreeNavigatorContentV2Legend'

const containerVariants = {
  visible: {
    transition: {
      staggerChildren: 0.12,
      delayChildren: 0.05,
    },
  },
}

type HorizontalTreeProps = {
  uiSchema: TreeNavigatorUISchema
  dataSchema: TreeNavigatorDataSchema
  tileContainerRef: React.RefObject<HTMLDivElement>
  selectedPath: number[]
  setSelectedPath: React.Dispatch<React.SetStateAction<number[]>>
}

type LinkPath = {
  id: string
  d: string
  color: string
  isActive: boolean
}

export function HorizontalTree({
  uiSchema,
  dataSchema,
  tileContainerRef,
  selectedPath,
  setSelectedPath,
}: HorizontalTreeProps) {
  const containerRef = React.useRef<HTMLDivElement | null>(null)
  const nodeRefs = React.useRef(new Map<number, HTMLDivElement | null>())
  const [searchParams] = useSearchParams()

  const isRtl = !!uiSchema.treeConfig.rightToLeft
  const attribute1Key = uiSchema.treeConfig.nodeConfig.attribute1
  const attribute2Key = uiSchema.treeConfig.nodeConfig.attribute2
  const attribute3Key = uiSchema.treeConfig.nodeConfig.attribute3
  const legend = uiSchema.treeConfig.legend
  const linkAction = uiSchema.treeConfig.linkConfig?.linkAction

  const { roots, nodesById, childrenMap, linkValueByEdge, linkMarkerPositionByEdge, linkFilterDataByEdge } =
    useTreeNavigatorGraph({
      dataSchema,
      linkAction,
    })

  React.useEffect(() => {
    if (roots.length > 0 && selectedPath.length === 0) {
      setSelectedPath([roots[0].id])
    }
  }, [roots, selectedPath.length, setSelectedPath])

  const columns = React.useMemo(() => {
    const cols: number[][] = []
    if (roots.length > 0) cols.push(roots.map((n) => n.id))

    for (let depth = 0; depth < selectedPath.length; depth += 1) {
      const parentId = selectedPath[depth]
      const children = childrenMap.get(parentId) ?? []
      if (children.length > 0) cols.push(children)
    }

    return cols
  }, [roots, selectedPath, childrenMap])

  const visibleNodeIdSet = React.useMemo(() => new Set(columns.flat()), [columns])
  const selectedNodeIdSet = React.useMemo(() => new Set(selectedPath), [selectedPath])

  const [svgSize, setSvgSize] = React.useState<{ width: number; height: number }>({
    width: 0,
    height: 0,
  })
  const [paths, setPaths] = React.useState<LinkPath[]>([])

  const setNodeRef = (id: number) => (el: HTMLDivElement | null) => {
    nodeRefs.current.set(id, el)
  }

  const isLinkActive = React.useCallback(
    (link: TreeLink): boolean => {
      const idx = selectedPath.indexOf(link.sourceNodeId)
      if (idx === -1) return false
      const nextIdx = idx + 1
      if (nextIdx >= selectedPath.length) return false
      return selectedPath[nextIdx] === link.targetNodeId
    },
    [selectedPath]
  )

  const updatePaths = React.useCallback(() => {
    const rootEl = containerRef.current
    if (!rootEl) return

    const rootRect = rootEl.getBoundingClientRect()
    setSvgSize({ width: rootEl.scrollWidth, height: rootEl.scrollHeight })

    const makePath = (startX: number, startY: number, endX: number, endY: number) => {
      const dx = (endX - startX) * 0.5
      const c1x = startX + dx
      const c2x = endX - dx
      return `M ${startX} ${startY} C ${c1x} ${startY}, ${c2x} ${endY}, ${endX} ${endY}`
    }

    const nextPaths: LinkPath[] = []
    const selectedSet = new Set(selectedPath)

    for (const link of dataSchema.data.links) {
      if (!selectedSet.has(link.sourceNodeId)) continue

      const srcEl = nodeRefs.current.get(link.sourceNodeId)
      const tgtEl = nodeRefs.current.get(link.targetNodeId)
      if (!srcEl || !tgtEl) continue
      if (!visibleNodeIdSet.has(link.sourceNodeId) || !visibleNodeIdSet.has(link.targetNodeId)) continue

      const srcRect = srcEl.getBoundingClientRect()
      const tgtRect = tgtEl.getBoundingClientRect()
      const startX = (isRtl ? srcRect.left : srcRect.right) - rootRect.left
      const startY = srcRect.top - rootRect.top + srcRect.height / 2
      const endX = (isRtl ? tgtRect.right : tgtRect.left) - rootRect.left
      const endY = tgtRect.top - rootRect.top + tgtRect.height / 2

      nextPaths.push({
        id: `${link.sourceNodeId}-${link.targetNodeId}`,
        d: makePath(startX, startY, endX, endY),
        color: 'hsl(var(--border))',
        isActive: isLinkActive(link),
      })
    }

    setPaths(nextPaths)
  }, [dataSchema, isLinkActive, isRtl, selectedPath, visibleNodeIdSet])

  React.useLayoutEffect(() => {
    updatePaths()
  }, [updatePaths])

  React.useEffect(() => {
    const rootEl = containerRef.current
    if (!rootEl || typeof ResizeObserver === 'undefined') return

    let frame = 0
    const observer = new ResizeObserver(() => {
      cancelAnimationFrame(frame)
      frame = requestAnimationFrame(updatePaths)
    })

    observer.observe(rootEl)
    return () => {
      cancelAnimationFrame(frame)
      observer.disconnect()
    }
  }, [updatePaths])

  const onNodeClick = (depth: number, nodeId: number) => {
    setSelectedPath((prev) => {
      if (prev[depth] === nodeId) return prev.slice(0, depth)
      const next = prev.slice(0, depth)
      next.push(nodeId)
      return next
    })
  }

  const getColumnWidth = (numberOfColumns: number) => {
    if (tileContainerRef.current) {
      return (tileContainerRef.current.clientWidth - numberOfColumns * 40 - 10) / numberOfColumns
    }
    return uiSchema.treeConfig.columnWidth ?? 240
  }

  return (
    <>
      <AnimatePresence>
        <div className="relative" ref={containerRef}>
          <div className="relative w-max">
            <svg
              className="pointer-events-none absolute left-0 top-0 z-0"
              width={svgSize.width}
              height={svgSize.height}
            >
              {paths.map((path) => (
                <path
                  key={path.id}
                  d={path.d}
                  fill="none"
                  stroke={path.isActive ? 'hsl(var(--primary))' : path.color}
                  strokeWidth={path.isActive ? 3 : 1}
                />
              ))}
            </svg>

            <div className={clsx('relative z-10 flex gap-10', { 'flex-row-reverse': isRtl })}>
              {columns.map((nodeIds, columnIndex) => {
                const numberOfColumns = columns.length
                const headerLabel =
                  columnIndex === 0
                    ? (uiSchema.treeConfig.startColumnLabel ?? 'Starting role')
                    : `${uiSchema.treeConfig.columnLabels ?? 'Move'} ${columnIndex}`
                const activeNodeId = selectedPath[columnIndex]
                const activeNode = activeNodeId ? nodesById.get(activeNodeId) : undefined
                const activeHeaderValue = activeNode
                  ? (activeNode.attributes?.[attribute1Key] as string | undefined)
                  : undefined

                return (
                  <div key={columnIndex} style={{ width: getColumnWidth(numberOfColumns), maxWidth: 450 }} className="">
                    <ColumnHeader label={headerLabel} value={activeHeaderValue} color={activeNode?.color} />
                    <motion.div
                      className="flex flex-col gap-3 pt-5"
                      initial="hidden"
                      animate="visible"
                      variants={containerVariants}
                    >
                      {nodeIds.map((id) => {
                        const node = nodesById.get(id)
                        if (!node) return null

                        const edgeKey = `${selectedPath[columnIndex - 1]}->${id}`
                        const progressPercent = columnIndex > 0 ? (linkValueByEdge.get(edgeKey) ?? 0) : 100
                        const markerPosition =
                          columnIndex > 0 ? (linkMarkerPositionByEdge.get(edgeKey) ?? 0) : undefined
                        const filterData = columnIndex > 0 ? linkFilterDataByEdge.get(edgeKey) : undefined
                        const linkTo =
                          columnIndex > 0 && linkAction?.handlerId
                            ? `${CATALOG_BASE_PATH}${linkAction.handlerId}?${searchParams.toString()}`
                            : undefined

                        return (
                          <NodeCard
                            key={id}
                            ref={setNodeRef(id)}
                            node={node}
                            attribute1Key={attribute1Key}
                            attribute2Key={attribute2Key}
                            attribute3Key={attribute3Key}
                            isSelected={selectedPath[columnIndex] === id}
                            isDimmed={!selectedNodeIdSet.has(id)}
                            progressPercent={progressPercent}
                            markerPosition={markerPosition}
                            onClick={() => onNodeClick(columnIndex, id)}
                            pageFilters={filterData as FormValues}
                            linkTo={linkTo}
                            isRtl={isRtl}
                          />
                        )
                      })}
                    </motion.div>
                  </div>
                )
              })}
            </div>
          </div>
        </div>
      </AnimatePresence>
      <TreeNavigatorLegend legend={legend} width={getColumnWidth(columns.length)} />
    </>
  )
}
